import React, { useState, useEffect } from 'react';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import { apiClient } from '../../api/client';
import { useToast } from '../../context/ToastContext';

const AdminStudents = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('All');
  const { addToast } = useToast();

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/students');
      setStudents(res.data || []);
    } catch (err) {
      addToast('Failed to load students', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    if (status === 'blocked' && !window.confirm('Are you sure you want to block this student?')) return;
    
    try {
      await apiClient.put(`/admin/students/${id}/status`, { status });
      addToast(`Student status updated to ${status}`, 'success');
      setStudents(students.map(s => s.id === id ? { ...s, status } : s));
    } catch (err) {
      addToast('Failed to update status', 'error');
    }
  };

  const filtered = students.filter(s => {
    const matchesSearch = (s.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (s.college_email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'All' || 
                         (filter === 'Active' && s.status === 'active') || 
                         (filter === 'Pending' && s.status === 'pending') || 
                         (filter === 'Blocked' && s.status === 'blocked');
    return matchesSearch && matchesFilter;
  });

  return (
    <div>
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="admin-page-title">Students</h1>
          <p className="admin-page-sub">Manage registered student accounts.</p>
        </div>
      </div>

      <div className="card-elevated">
        <div className="flex justify-between items-center mb-6">
          <div className="tabs mb-0 border-none">
            {['All', 'Active', 'Pending', 'Blocked'].map(tab => (
              <button 
                key={tab} 
                className={`tab-btn ${filter === tab ? 'active' : ''}`}
                onClick={() => setFilter(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
          
          <div className="search-bar w-64">
            <span className="search-icon text-sm">🔍</span>
            <input 
              type="text" 
              placeholder="Search students..." 
              className="text-sm py-2"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No students found matching your criteria.</div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>College Info</th>
                  <th>Joined Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(student => (
                  <tr key={student.id}>
                    <td>
                      <div className="font-semi text-gray-900">{student.full_name}</div>
                      <div className="text-xs text-gray-500">{student.college_email}</div>
                    </td>
                    <td>
                      <div className="text-sm">{student.college_name}</div>
                      <div className="text-xs text-gray-500">{student.branch || 'N/A'} • {student.year ? `Year ${student.year}` : 'N/A'}</div>
                    </td>
                    <td className="text-sm">{new Date(student.created_at).toLocaleDateString()}</td>
                    <td>
                      <Badge variant={
                        student.status === 'active' ? 'success' : 
                        student.status === 'blocked' ? 'danger' : 'warning'
                      }>
                        {student.status || 'pending'}
                      </Badge>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        {student.status !== 'active' && (
                          <Button size="sm" variant="outline" className="!py-1 !px-2 text-xs" onClick={() => handleStatusChange(student.id, 'active')}>
                            Approve
                          </Button>
                        )}
                        {student.status !== 'blocked' && (
                          <Button size="sm" variant="ghost" className="!py-1 !px-2 text-xs text-danger" onClick={() => handleStatusChange(student.id, 'blocked')}>
                            Block
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminStudents;
